import { useCallback, useEffect, useMemo, useState } from 'react';

import type { AnalyticsBrowser } from '@customerio/cdp-analytics-browser';

import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useCustomerIOClient } from '@shared/analytics';
import { isProduction } from '@shared/config';
import { useLocale } from '@shared/hooks/locale';

import {
  MOCK_INBOX_STORAGE_KEY,
  type NotificationInboxEntry,
  createMockInboxMessages,
} from './mockInboxMessages';
import {
  type NotificationInboxItem,
  addIds,
  countUnreadNotificationInboxItems,
  describeError,
  dropConfirmedIds,
  getNotificationInboxItems,
  markMessagesOpened,
  removeIds,
  retry,
  selectMessagesToOpen,
  selectUnopenedIds,
  withTimeout,
} from './notificationInboxMessages';

export const NOTIFICATION_INBOX_TOPIC = 'pro-app';

export type NotificationInboxStatus =
  | 'unavailable'
  | 'loading'
  | 'ready'
  | 'error';

type InboxState = {
  status: NotificationInboxStatus;
  messages: NotificationInboxEntry[];
  error: string | null;
};

const UNAVAILABLE: InboxState = {
  status: 'unavailable',
  messages: [],
  error: null,
};

const isInboxMockEnabled = (): boolean => {
  if (isProduction) {
    return false;
  }
  try {
    return sessionStorage.getItem(MOCK_INBOX_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const PLUGIN_ATTEMPTS = 10;
const PLUGIN_RETRY_MS = 500;
const CLIENT_READY_TIMEOUT_MS = 10000;

const retryOptions = (isCancelled: () => boolean) => ({
  attempts: PLUGIN_ATTEMPTS,
  delayMs: PLUGIN_RETRY_MS,
  isCancelled,
});

const waitForIdentity = (
  client: AnalyticsBrowser,
  userId: string,
  isCancelled: () => boolean,
) =>
  retry(async () => {
    const user = await client.user();
    if (user.id() !== userId) {
      throw new Error('Customer.io is not identified as the current user');
    }
  }, retryOptions(isCancelled));

const waitForInbox = (client: AnalyticsBrowser, isCancelled: () => boolean) =>
  retry(
    () => client.inbox(NOTIFICATION_INBOX_TOPIC),
    retryOptions(isCancelled),
  );

export const useNotificationInbox = () => {
  const { t } = useLocale();
  const { client } = useCustomerIOClient();
  const { me } = useAppData();
  const userId = me?.id;

  const [state, setState] = useState<InboxState>(UNAVAILABLE);
  const [reloadKey, setReloadKey] = useState(0);
  const [locallyOpened, setLocallyOpened] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (isInboxMockEnabled()) {
      setState({
        status: 'ready',
        messages: createMockInboxMessages(new Date()),
        error: null,
      });
      setLocallyOpened(new Set());
      return undefined;
    }

    if (client == null || userId == null) {
      setState(UNAVAILABLE);
      return undefined;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    setState({ status: 'loading', messages: [], error: null });
    setLocallyOpened(new Set());

    const load = async () => {
      try {
        await withTimeout(
          client.ready(),
          CLIENT_READY_TIMEOUT_MS,
          'Customer.io did not load',
        );
        await waitForIdentity(client, userId, () => cancelled);
        if (cancelled) {
          return;
        }
        const inbox = await waitForInbox(client, () => cancelled);
        if (cancelled) {
          return;
        }
        const messages = await inbox.messages();
        if (cancelled) {
          return;
        }
        setState({ status: 'ready', messages, error: null });
        setLocallyOpened(new Set());
        unsubscribe = inbox.onUpdates(next => {
          if (!cancelled) {
            setState({ status: 'ready', messages: next, error: null });
            setLocallyOpened(current => dropConfirmedIds(current, next));
          }
        });
        if (cancelled) {
          unsubscribe();
        }
      } catch (error) {
        console.warn('[CustomerIO] inbox failed:', error);
        if (!cancelled) {
          setState({
            status: 'error',
            messages: [],
            error: describeError(error),
          });
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [client, userId, reloadKey]);

  const items: NotificationInboxItem[] = useMemo(
    () =>
      getNotificationInboxItems(
        state.messages.map(message => ({
          messageId: message.messageId,
          opened: message.opened || locallyOpened.has(message.messageId),
          sentAt: message.sentAt,
          type: message.type,
          properties: message.properties,
        })),
        t,
      ),
    [state.messages, locallyOpened, t],
  );

  const unreadCount = useMemo(
    () => countUnreadNotificationInboxItems(items),
    [items],
  );

  const markOpened = useCallback(
    async (messageIds: string[]) => {
      const targets = selectMessagesToOpen(
        state.messages,
        messageIds,
        locallyOpened,
      );
      if (targets.length === 0) {
        return;
      }

      const targetIds = targets.map(message => message.messageId);
      setActionError(null);
      setLocallyOpened(current => addIds(current, targetIds));

      const { failedIds, reasons } = await markMessagesOpened(targets);
      if (failedIds.length > 0) {
        console.warn('[CustomerIO] mark inbox message opened failed', reasons);
        setActionError(reasons[0]);
        setLocallyOpened(current => removeIds(current, failedIds));
      }
    },
    [state.messages, locallyOpened],
  );

  const trackClick = useCallback(
    (messageId: string) => {
      const message = state.messages.find(
        candidate => candidate.messageId === messageId,
      );
      try {
        message?.trackClick();
      } catch (error) {
        console.warn('[CustomerIO] inbox trackClick failed', error);
      }
    },
    [state.messages],
  );

  const markOneOpened = useCallback(
    (messageId: string) => markOpened([messageId]),
    [markOpened],
  );

  const markAllOpened = useCallback(
    () => markOpened(selectUnopenedIds(items)),
    [markOpened, items],
  );

  const reload = useCallback(() => setReloadKey(key => key + 1), []);
  const dismissActionError = useCallback(() => setActionError(null), []);

  return {
    status: state.status,
    items,
    unreadCount,
    markOneOpened,
    trackClick,
    markAllOpened,
    reload,
    error: state.error,
    actionError,
    dismissActionError,
  };
};
